import React, { useState } from "react";
import { Phone, MessageSquare, Globe, Siren, Mail } from "lucide-react";
import { toast } from "sonner";
import { submitContact } from "../lib/api";

const RESOURCES = [
    {
        Icon: Phone,
        title: "988 — Suicide & Crisis Lifeline",
        detail: "Call or text. 24/7. Free and confidential.",
        action: "Call or text 988",
        href: "tel:988",
        accent: true,
        testId: "resource-988",
    },
    {
        Icon: MessageSquare,
        title: "Crisis Text Line",
        detail: "Text HOME to 741741. Real human, immediately.",
        action: "Text 741741",
        href: "sms:741741&body=HOME",
        testId: "resource-741741",
    },
    {
        Icon: Siren,
        title: "Emergency (US)",
        detail: "If you or someone you love is in immediate danger.",
        action: "Call 911",
        href: "tel:911",
        testId: "resource-911",
    },
    {
        Icon: Globe,
        title: "International Help",
        detail: "Find a helpline anywhere in the world.",
        action: "findahelpline.com",
        href: "https://findahelpline.com",
        external: true,
        testId: "resource-international",
    },
];

const Help = () => {
    const [form, setForm] = useState({ name: "", email: "", message: "" });
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await submitContact(form);
            toast.success("Message received. You are not alone.");
            setForm({ name: "", email: "", message: "" });
        } catch (err) {
            toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Could not send. Please try again or call 988.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-black min-h-screen" data-testid="help-page">
            <section className="border-b border-white/10 py-20 md:py-28">
                <div className="max-w-5xl mx-auto px-6 lg:px-10">
                    <div className="flex items-center gap-3 mb-6 text-xs tracking-[0.4em] text-red-600 font-display uppercase">
                        <span className="h-px w-12 bg-red-600" />
                        Get Help Now
                    </div>
                    <h1 className="font-display text-5xl md:text-7xl uppercase tracking-tighter leading-[0.9]">
                        Reaching out is the <span className="text-red-600">first act of strength.</span>
                    </h1>
                    <p className="mt-6 max-w-2xl text-white/75 text-lg">
                        If you are in pain right now — please use the resources below. Real
                        humans. Real help. No shame.
                    </p>
                </div>
            </section>

            {/* Resources */}
            <section className="py-16 md:py-20">
                <div className="max-w-5xl mx-auto px-6 lg:px-10">
                    <div className="grid sm:grid-cols-2 gap-px bg-white/10 border border-white/10">
                        {RESOURCES.map((r) => (
                            <a
                                key={r.title}
                                href={r.href}
                                target={r.external ? "_blank" : undefined}
                                rel={r.external ? "noopener noreferrer" : undefined}
                                className={`p-8 transition-colors block group ${
                                    r.accent
                                        ? "bg-red-700 hover:bg-red-800 text-white"
                                        : "bg-black hover:bg-[#0a0a0a]"
                                }`}
                                data-testid={r.testId}
                            >
                                <r.Icon
                                    size={32}
                                    className={r.accent ? "text-white" : "text-red-600"}
                                    strokeWidth={1.5}
                                />
                                <h3 className="mt-6 font-display text-2xl uppercase tracking-tight">
                                    {r.title}
                                </h3>
                                <p className={`mt-3 text-sm ${r.accent ? "text-white/90" : "text-white/65"}`}>
                                    {r.detail}
                                </p>
                                <div className={`mt-6 font-display text-xs tracking-[0.2em] uppercase ${r.accent ? "text-white" : "text-red-600"} group-hover:underline`}>
                                    {r.action} →
                                </div>
                            </a>
                        ))}
                    </div>
                </div>
            </section>

            {/* Contact */}
            <section className="bg-[#0a0a0a] border-t border-white/10 py-20">
                <div className="max-w-3xl mx-auto px-6 lg:px-10">
                    <div className="flex items-center gap-3 mb-6 text-xs tracking-[0.4em] text-red-600 font-display uppercase">
                        <span className="h-px w-12 bg-red-600" />
                        Send a Message
                    </div>
                    <h2 className="font-display text-3xl md:text-5xl uppercase tracking-tight leading-[0.95]">
                        Want to share your story or reach out?
                    </h2>
                    <p className="mt-4 text-white/65">
                        Note: This form is not monitored 24/7. For crisis, please use 988 above.
                    </p>

                    <form onSubmit={submit} className="mt-10 grid gap-4" data-testid="contact-form">
                        <input
                            required
                            placeholder="Your name"
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            className="px-5 py-4 bg-black border border-white/15 focus:border-red-600 outline-none text-white placeholder-white/30"
                            data-testid="contact-name"
                        />
                        <input
                            required
                            type="email"
                            placeholder="Your email"
                            value={form.email}
                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                            className="px-5 py-4 bg-black border border-white/15 focus:border-red-600 outline-none text-white placeholder-white/30"
                            data-testid="contact-email"
                        />
                        <textarea
                            required
                            rows={6}
                            placeholder="What's going on..."
                            value={form.message}
                            onChange={(e) => setForm({ ...form, message: e.target.value })}
                            className="px-5 py-4 bg-black border border-white/15 focus:border-red-600 outline-none text-white placeholder-white/30 resize-none"
                            data-testid="contact-message"
                        />
                        <button
                            type="submit"
                            disabled={loading}
                            className="btn-primary justify-center disabled:opacity-50"
                            data-testid="contact-submit"
                        >
                            <Mail size={16} />
                            {loading ? "Sending..." : "Send Message"}
                        </button>
                    </form>
                </div>
            </section>
        </div>
    );
};

export default Help;
